import React, { useCallback, useState } from 'react';
import moment from 'moment';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Link, useSearchParams } from 'react-router-dom';
import cx from 'classnames';
import { AlertTriangle, FileText, MessageSquarePlus, Download } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@@/store';
import { embedPath, isEmbedded } from '@@/utils/embed';
import { useFetchIncidentsQuery } from '@@/services/incident';
import {
  translateIncidentStatus,
  IncidentStatuses,
} from '@@/constants/incident';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  ColumnFiltersState,
  getFilteredRowModel,
} from '@tanstack/react-table';
import {
  Filter,
  dateMatchFilter,
  propertyNameMatchFilter,
  buildingNameMatchFilter,
  apartmentNumberMatchFilter,
  statusMatchFilter,
} from '@@/components/Table/Filter';

const columnHelper = createColumnHelper<Alacaza.Incident>();
const columns = [
  columnHelper.accessor('title', {
    header: 'Titre',
    cell: (row) => {
      const title = row.getValue();
      return <p className="font-semibold text-neutral-900 text-body">{title}</p>;
    },
  }),
  columnHelper.accessor('description', {
    header: 'Description',
    cell: (row) => {
      const description = row.getValue();

      return (
        <p className="text-neutral-900 text-body-sm text-center w-36 mx-auto truncate">
          {description}
        </p>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Résidence',
    filterFn: propertyNameMatchFilter,
    cell: (row) => {
      const { propertyId } = row.getValue();
      return (
        <p className="text-neutral-900 text-body-sm">
          {(propertyId as unknown as Alacaza.Property)?.name}
        </p>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Bâtiment',
    filterFn: buildingNameMatchFilter,
    cell: (row) => {
      const { creator } = row.getValue();
      if (!creator?.buildingName) {
        return null;
      }
      return <p className="text-neutral-900 text-body-sm">{creator.buildingName}</p>;
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Appartement',
    filterFn: apartmentNumberMatchFilter,
    cell: (row) => {
      const { creator } = row.getValue();
      if (!creator?.apartmentNumber) {
        return null;
      }
      return <p className="text-neutral-900 text-body-sm">{creator.apartmentNumber}</p>;
    },
  }),
  columnHelper.accessor('date', {
    header: 'Date',
    filterFn: dateMatchFilter,
    cell: (row) => {
      const date = row.getValue();

      return (
        <p className="text-neutral-900 text-body-sm">{moment(date).format('lll')}</p>
      );
    },
  }),
  columnHelper.accessor('status', {
    header: 'Statut',
    filterFn: statusMatchFilter,
    cell: (row) => {
      const status = row.getValue();

      return (
        <div className="flex justify-center">
          <span
            className={cx(
              'inline-flex items-center px-3 py-1 rounded-badge text-body-xs font-medium',
              status === IncidentStatuses.RESOLVED && 'bg-success-100 text-success-800',
              status === IncidentStatuses.IN_PROGRESS && 'bg-warning-100 text-warning-800',
              status === IncidentStatuses.DELETED && 'bg-neutral-100 text-neutral-800',
            )}
          >
            {translateIncidentStatus(status)}
          </span>
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Actions',
    enableColumnFilter: false,
    cell: (row) => {
      const { id } = row.getValue();

      return (
        <div className="flex justify-center">
          <Link
            to={embedPath(`/incidents/${id}`)}
            className="bg-neutral-500 hover:bg-neutral-600 px-4 py-2 rounded-button shadow-button transition-colors inline-flex items-center"
          >
            <span className="text-white text-body-sm font-medium">Détails</span>
          </Link>
        </div>
      );
    },
  }),
];

const IncidentsPage: React.FC = () => {
  const dispatch = useDispatch();
  const [query] = useSearchParams();
  const propertyId = query.get('propertyId') || undefined;
  const status = query.get('status') || undefined;
  const {
    data: incidents,
    isLoading,
    error,
  } = useFetchIncidentsQuery({
    propertyId,
    status,
  });
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const table = useReactTable({
    data: incidents?.docs || [],
    columns,
    state: {
      sorting,
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
  });

  const exportIncidentsAsCsv = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${import.meta.env.VITE_API_URL}partners/incidents/csv`,
        {
          params: {
            propertyId,
            status,
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const url = window.URL.createObjectURL(
        new Blob(
          [
            new Uint8Array([0xef, 0xbb, 0xbf]), // UTF-8 BOM
            data,
          ],
          {
            type: 'text/csv;charset=UTF-8',
          },
        ),
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `incidents-${moment(new Date()).format('YYYY-MM-DD')}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      // Sans ce nettoyage, chaque export laissait un <a> mort dans le DOM et
      // gardait le Blob en mémoire jusqu'au rechargement de la page. Différé :
      // révoquer l'URL dans la foulée du `click()` annule le téléchargement
      // sur les navigateurs qui n'ont pas encore lu le Blob.
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (e) {
      // Seule requête embed qui passe par axios : sans ce relais, un 401
      // n'atteindrait ni baseQueryWithReauth ni rtkQueryErrorHandler, donc
      // l'iframe ne redemanderait jamais de token au parent.
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        dispatch({ type: 'auth/logout' });
        return;
      }

      toast.error("L'export CSV a échoué", {
        position: 'top-right',
      });
    }
  }, [accessToken, dispatch, propertyId, status]);

  return (
    <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div className="flex flex-row items-center">
            <AlertTriangle size={28} className="text-neutral-900" />
            <h1 className="text-heading-lg text-neutral-900 ml-3">Incidents</h1>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <Link
              className="bg-primary-600 hover:bg-primary-700 px-4 py-2.5 rounded-button shadow-button transition-colors flex items-center justify-center space-x-2"
              to={embedPath('/incidents/new')}
            >
              <AlertTriangle size={18} className="text-white" />
              <span className="text-white text-body-sm font-medium">Nouvel incident</span>
            </Link>
            {/* Sort du périmètre incidents : masqué en embed. */}
            {!isEmbedded() && (
              <Link
                className="bg-neutral-600 hover:bg-neutral-700 px-4 py-2.5 rounded-button shadow-button transition-colors flex items-center justify-center space-x-2"
                to="/template-messages"
              >
                <MessageSquarePlus size={18} className="text-white" />
                <span className="text-white text-body-sm font-medium">Messages types</span>
              </Link>
            )}
            <button
              className="bg-neutral-600 hover:bg-neutral-700 px-4 py-2.5 rounded-button shadow-button transition-colors flex items-center justify-center space-x-2"
              onClick={exportIncidentsAsCsv}
            >
              <Download size={18} className="text-white" />
              <span className="text-white text-body-sm font-medium">Export CSV</span>
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="bg-white rounded-card shadow-card border border-neutral-200 p-8 text-center">
            <p className="text-body text-neutral-500">
              Chargement des incidents…
            </p>
          </div>
        )}

        {!isLoading && !!error && (
          <div className="bg-white rounded-card shadow-card border border-danger-200 p-8 text-center">
            <p className="text-body text-danger-600">
              Impossible de charger les incidents. Réessayez dans un instant.
            </p>
          </div>
        )}

        {!isLoading && !error && (
          <>
            {/* Mobile Cards - visible < lg */}
            <div className="lg:hidden space-y-4">
              {table.getRowModel().rows.map((row) => {
                const incident = row.original;
                return (
                  <div
                    key={incident.id}
                    className="bg-white rounded-card shadow-card border border-neutral-200 p-4 hover:shadow-card-hover transition-shadow"
                  >
                    <div className="mb-3">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="font-semibold text-neutral-900 text-body">{incident.title}</p>
                        <span
                          className={cx(
                            'inline-flex items-center px-3 py-1 rounded-badge text-body-xs font-medium flex-shrink-0',
                            incident.status === IncidentStatuses.RESOLVED && 'bg-success-100 text-success-800',
                            incident.status === IncidentStatuses.IN_PROGRESS && 'bg-warning-100 text-warning-800',
                            incident.status === IncidentStatuses.DELETED && 'bg-neutral-100 text-neutral-800',
                          )}
                        >
                          {translateIncidentStatus(incident.status)}
                        </span>
                      </div>

                      <p className="text-neutral-600 text-body-sm mb-1">
                        {(incident.propertyId as unknown as Alacaza.Property)?.name}
                      </p>
                      {incident.creator?.buildingName && (
                        <p className="text-neutral-600 text-body-sm mb-1">
                          {incident.creator.buildingName}
                        </p>
                      )}
                      <p className="text-neutral-500 text-body-xs">
                        {moment(incident.date).format('lll')}
                      </p>
                    </div>

                    <div className="flex justify-end">
                      <Link
                        to={embedPath(`/incidents/${incident.id}`)}
                        className="bg-neutral-500 hover:bg-neutral-600 px-4 py-2 rounded-button shadow-button transition-colors inline-flex items-center"
                      >
                        <span className="text-white text-body-sm font-medium">Détails</span>
                      </Link>
                    </div>
                  </div>
                );
              })}

              {table.getRowModel().rows.length === 0 && (
                <div className="bg-white rounded-card shadow-card border border-neutral-200 p-8 text-center">
                  <p className="text-body text-neutral-500">Aucun incident trouvé</p>
                </div>
              )}
            </div>

            {/* Desktop Table - visible >= lg */}
            <div className="hidden lg:block bg-white rounded-card shadow-card border border-neutral-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-neutral-200">
                <p className="text-body-sm font-semibold text-neutral-700">
                  {incidents?.docs?.length || 0} incident{(incidents?.docs?.length || 0) > 1 ? 's' : ''}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className="px-4 py-3 text-left text-body-sm font-semibold text-white bg-neutral-700 border border-neutral-200"
                          >
                            {header.isPlaceholder ? null : (
                              <>
                                <div
                                  className={
                                    header.column.getCanSort()
                                      ? 'cursor-pointer select-none flex items-center'
                                      : ''
                                  }
                                  onClick={header.column.getToggleSortingHandler()}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                                  {{
                                    asc: <span className="ml-2 text-xs">↑</span>,
                                    desc: <span className="ml-2 text-xs">↓</span>,
                                  }[header.column.getIsSorted() as string] ?? null}
                                </div>

                                {header.column.getCanFilter() ? (
                                  <Filter column={header.column} />
                                ) : null}
                              </>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="hover:bg-neutral-50 transition-colors">
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="px-4 py-3 border-b border-neutral-200">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IncidentsPage;
